import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import {
	PrefilledNewClient,
	getClientType,
	getLLCClientName,
	getPrefilledNewClient,
} from '@core/util/llc';
import { isSet } from '@core/util/value';
import { Address } from '@models/address';
import { ClientType } from '@models/clients';
import {
	CloserSyncInstruction,
	CloserSyncTransaction,
} from '@models/mortgages/closer-sync-instruction';
import {
	ClientMappingType,
	MortgageInstructionGuarantor,
	MortgageInstructionMortgagor,
} from '@models/mortgages/mortgage-base';
import { TrustAccount } from '@models/trustAccount';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { UpdateFields } from '@repos/mortgages/closer-sync/closer-sync.service';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
	CloserSyncSyncMortgageFieldsSelectors,
	FileComparisons,
} from './closer-sync-sync-mortgage-fields.selectors';

export interface VM_MortgagorGuarantor {
	id: string;
	name: string;
	business: boolean;
	signingOfficer: boolean;
	signingOfficerName?: string;
	signingOfficerPrefilledNewClient?: PrefilledNewClient;
	signingOfficerClientId: string;
	signingOfficerCloserSyncId: string;
	client: MortgageInstructionMortgagor | MortgageInstructionGuarantor;
	prefilledNewClient: PrefilledNewClient;
	guarantor: boolean;
	closerClientId: string;
	mappingType: ClientMappingType;
	signingOfficerMappingType: ClientMappingType;
}

export interface VM {
	showPurchasePrice: boolean;
	showClosingDate: boolean;
	showAddress: boolean;
	showLegalDescription: boolean;
	clients: VM_MortgagorGuarantor[];
	trustAccount: TrustAccount;
	fileComparisons: FileComparisons;
	showMappings: boolean;
}

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-sync-mortgage-fields',
	templateUrl: './closer-sync-sync-mortgage-fields.component.html',
	styleUrls: ['./closer-sync-sync-mortgage-fields.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncSyncMortgageFieldsComponent
	implements OnInit, OnDestroy
{
	public _newMatter: boolean;
	public instruction$: Observable<CloserSyncInstruction>;
	public transaction$: Observable<CloserSyncTransaction>;
	private _instruction: CloserSyncInstruction;
	private _transaction: CloserSyncTransaction;

	@Select(CloserSyncSyncMortgageFieldsSelectors.getFileComparisons)
	fileComparisons$: Observable<any>;
	public fileComparisonsSub;

	private fieldsSubject = new BehaviorSubject<UpdateFields>({
		purchasePrice: false,
		closingDate: false,
		address: false,
		legalDescription: false,
		guarantors: [],
		useTrustAccountOnInstructions: false,
		mappings: [],
	});
	public fields$: Observable<UpdateFields> = this.fieldsSubject.asObservable();
	private built = false;

	private vmSubject = new BehaviorSubject<VM>({
		showPurchasePrice: false,
		showAddress: false,
		showClosingDate: false,
		showLegalDescription: false,
		showMappings: false,
		clients: [],
		trustAccount: null,
		fileComparisons: null,
	});

	public vm$: Observable<VM> = this.vmSubject.asObservable();

	@Output() selectedFields = new EventEmitter<UpdateFields>();

	@Input() set transaction(transaction$: Observable<CloserSyncTransaction>) {
		this.transaction$ = transaction$.pipe(
			tap((transaction) => {
				this._transaction = transaction;
				this.build();
			}),
		);
	}

	@Input() set instruction(instruction$: Observable<CloserSyncInstruction>) {
		this.instruction$ = instruction$.pipe(
			untilDestroyed(this),
			tap((instruction) => {
				this._instruction = instruction;
				this.build();
			}),
		);
	}

	@Input() set newMatter(newMatter: boolean) {
		this._newMatter = newMatter;
		this.build();
	}

	constructor() {}

	ngOnDestroy(): void {
		this.vmSubject.unsubscribe();
		this.fieldsSubject.unsubscribe();
	}

	ngOnInit(): void {
		this.fileComparisonsSub = this.fileComparisons$
			.pipe(untilDestroyed(this))
			.subscribe((values) => {
				const vm = {
					...this.VM,
					fileComparisons: values,
				};
				this.vmSubject.next(vm);
			});
	}

	get VM(): VM {
		return cloneDeep(this.vmSubject.getValue());
	}

	get Fields(): UpdateFields {
		return cloneDeep(this.fieldsSubject.getValue());
	}

	togglePurchasePrice() {
		this.updateFields({ purchasePrice: !this.Fields.purchasePrice });
	}

	toggleAddress() {
		this.updateFields({ address: !this.Fields.address });
	}

	toggleLegalDescription() {
		this.updateFields({ legalDescription: !this.Fields.legalDescription });
	}

	toggleClosingDate() {
		this.updateFields({ closingDate: !this.Fields.closingDate });
	}

	toggleTrustAccount() {
		this.updateFields({
			useTrustAccountOnInstructions: !this.Fields.useTrustAccountOnInstructions,
		});
	}

	updateFields(fields: Partial<UpdateFields>) {
		this.fieldsSubject.next({ ...this.Fields, ...fields });
		this.selectedFields.emit(this.Fields);
	}

	buildMortgagors(
		client: MortgageInstructionMortgagor | MortgageInstructionGuarantor,
		guarantor: boolean,
	): VM_MortgagorGuarantor {
		const id = getLLCClientName(client)
			?.replace(/[ ]/g, '_')
			?.replace(/[^a-zA-Z0-9]/g, '');
		const clientType = guarantor
			? getClientType((client as MortgageInstructionGuarantor).guarantorType)
			: getClientType((client as MortgageInstructionMortgagor).mortgagorType);

		const hasSigningOfficer =
			clientType === ClientType.CORPORATION && isSet(client?.firstName);
		const signingOfficerName = hasSigningOfficer
			? [client.firstName, client.middleName, client.lastName]
					?.filter((x) => !!x)
					.join(' ')
			: null;
		const signingOfficerCloserSyncId = hasSigningOfficer
			? signingOfficerName.replace(/[ ]/g, '_').replace(/[^a-zA-Z0-9]/g, '')
			: null;
		const signingOfficerClientId = hasSigningOfficer
			? this._transaction?.clientMappings?.find(
					(x) => x.llcId === signingOfficerCloserSyncId,
				)?.closerClientId
			: null;
		const mapping = this._transaction?.clientMappings?.find(
			(x) => x.llcId === id,
		);

		return {
			id,
			name: getLLCClientName(client),
			prefilledNewClient: getPrefilledNewClient(client, clientType),
			client,
			business: isSet(client?.companyName),
			closerClientId: mapping?.closerClientId,
			mappingType: clientType === ClientType.PERSON ? 'person' : 'company',
			signingOfficer: hasSigningOfficer,
			signingOfficerName: signingOfficerName,
			signingOfficerPrefilledNewClient: hasSigningOfficer
				? getPrefilledNewClient(client, ClientType.PERSON)
				: null,
			signingOfficerCloserSyncId,
			signingOfficerClientId,
			signingOfficerMappingType: 'signingOfficer',
			guarantor,
		};
	}

	buildTrustAccount() {
		const financialInstitution =
			this._instruction?.trustAccount?.financialInstitution;
		const trustAccount = {
			id: '',
			address: financialInstitution?.address as Address,
			nickname: '',
			payeeName: '',
			institutionName: financialInstitution?.name,
			accountNumber: financialInstitution?.account,
			last3DigitsAccountNumber: financialInstitution?.accountDisplayText,
			transit: financialInstitution?.transit,
			institutionNumber: financialInstitution?.institution,
			institutionId: '',
			institutionShortName: '',
			voidCheque: '',
		};
		if (Object.values(trustAccount).every((v) => !v)) {
			return null;
		}
		return trustAccount;
	}

	build() {
		if (this.built) return;
		if (!this._instruction) return;
		if (!isSet(this._newMatter)) return;
		if (!isSet(this._transaction)) return;

		const vm = this.VM;

		vm.showPurchasePrice =
			!!this._instruction?.purchasePrice || !!vm.fileComparisons?.purchasePrice;
		vm.showClosingDate =
			isSet(this._instruction?.closingDate) ||
			!!vm.fileComparisons?.closingDate;
		vm.showAddress =
			isSet(this._instruction?.properties?.[0]?.address) ||
			!!vm.fileComparisons?.address;
		vm.showLegalDescription =
			isSet(this._instruction?.properties?.[0]?.legalDescription) ||
			!!vm.fileComparisons?.legalDescription;
		const mortgagors = this._instruction?.mortgagors?.map((m) =>
			this.buildMortgagors(m, false),
		);
		const guarantors = this._instruction?.guarantors?.map((m) =>
			this.buildMortgagors(m, true),
		);
		vm.showMappings = mortgagors.length > 0 || guarantors.length > 0;
		vm.clients = [...mortgagors, ...guarantors];
		vm.trustAccount = this.buildTrustAccount();
		this.Fields.mappings = this._transaction?.clientMappings;

		this.vmSubject.next(vm);
		this.updateFields(this.Fields);

		this.built = true;
	}

	onRemove(client: VM_MortgagorGuarantor, signingOfficer: boolean = false) {
		const closerId = signingOfficer
			? client.signingOfficerClientId
			: client.closerClientId;
		const vm = this.VM;
		const _client = vm.clients.find(
			(c) => c.closerClientId === client.closerClientId,
		);
		_client.signingOfficerClientId = null;

		if (!signingOfficer) {
			_client.closerClientId = null;
		}

		this.vmSubject.next(vm);

		const fields = this.Fields;
		const mappings = fields.mappings.filter(
			(m) => m.closerClientId !== closerId,
		);
		let guarantors = fields.guarantors.filter((g) => g !== closerId);

		if (fields.guarantors.includes(closerId)) {
			guarantors = fields.guarantors.filter((g) => g !== closerId);
		}
		this.updateFields({ mappings, guarantors });
	}

	borrowerChanged(
		$event: any,
		client: VM_MortgagorGuarantor,
		signingOfficer: boolean = false,
	) {
		if (!$event) {
			return;
		}

		const fields = this.Fields;

		const mappings = fields.mappings || [];
		const type = signingOfficer
			? client.signingOfficerMappingType
			: client.mappingType;
		const closerSyncId = signingOfficer
			? client.signingOfficerCloserSyncId
			: client.id;

		let mapping = mappings.find((m) => m.llcId === closerSyncId);

		if (!mapping) {
			mapping = {
				llcId: closerSyncId,
				closerClientId: $event,
				type,
			};
			mappings.push(mapping);
		} else {
			(mapping.llcId = closerSyncId), (mapping.closerClientId = $event);
			mapping.type = type;
		}

		const guarantors = [];
		if (client.guarantor && !signingOfficer) {
			guarantors.push($event);
		}

		this.updateFields({
			mappings,
			guarantors: [...this.Fields.guarantors, ...guarantors],
		});
	}

	clientId(index: number, client: VM_MortgagorGuarantor) {
		return client.id;
	}
}
